/**
 * TanStack Query hooks for `global-goal-tracking` (FS-302).
 *
 * Thin wrappers over ./api. Mutations invalidate the full slug subtree so
 * every variant of the list query refetches.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	globalGoalTrackingQueryKeys,
	type CreateGoalInput,
	type CreateGoalResponse,
	type DeleteGoalResponse,
	type ListGoalsResponse,
} from "./api";

export function useGlobalGoalTrackingList(params?: {
	activeOnly?: boolean;
}): UseQueryResult<ListGoalsResponse, Error> {
	const activeOnly = params?.activeOnly === true;
	return useQuery({
		queryKey: globalGoalTrackingQueryKeys.list(activeOnly),
		queryFn: () => list({ activeOnly }),
		staleTime: 10_000,
	});
}

export function useGlobalGoalTrackingCreate(): UseMutationResult<
	CreateGoalResponse,
	Error,
	CreateGoalInput
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: globalGoalTrackingQueryKeys.all });
		},
	});
}

export function useGlobalGoalTrackingDelete(): UseMutationResult<
	DeleteGoalResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: globalGoalTrackingQueryKeys.all });
		},
	});
}
